import {
  type ConversionContext,
  extractIngredientTerms,
  ingredientLookupKey,
  type IngredientNutritionRow,
  type NutritionFact,
  type NutritionTotals,
  type Quantity,
  quantityToGrams,
  type Recipe,
  tokenizeIngredient,
  totalNutrition,
} from '@cookyourbooks/domain';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { useLocalQueryEnabled } from '../local/SyncProvider.js';
import { recordOnce, recordSuggestionEvent } from '../signals/capture.js';
import { nutritionFactKey, readCachedFact, resolveMapping, searchNutrition } from './api.js';
import { listConversionRulesForOwner } from './conversions.js';
import { searchLocalEssentials } from './localCache.js';

/**
 * Resolve nutrition for every measured ingredient on a recipe and
 * compute the recipe-level total. Vague ingredients ("salt to taste")
 * appear in the row list but contribute nothing.
 *
 * Resolution per ingredient:
 *   1. Look up the persisted mapping (`resolve_nutrition_mapping`).
 *      User-scope wins over platform-default.
 *   2. If mapping exists → read cached fact for (source, source_id).
 *   3. If no mapping → live-search USDA / OFF through the edge
 *      function. The edge function caches each hit; we pick the top
 *      result as the auto-match. The user can override later.
 *
 * Grams computation leans on the existing `global_conversions` table
 * (ingredient-specific densities), then the cache's `portions` array,
 * then pure mass / water-equivalent fallback (last marked approximate).
 */

export interface ResolvedRecipeNutrition {
  rows: IngredientNutritionRow[];
  totals: NutritionTotals;
  /** Whether any ingredient row still has a non-final auto-match — UX
   *  can show a "review matches" CTA. */
  needsReview: boolean;
}

export function useRecipeNutrition(recipe: Recipe | undefined) {
  const { user } = useAuth();
  const localReady = useLocalQueryEnabled();

  return useQuery<ResolvedRecipeNutrition>({
    queryKey: [
      'recipe-nutrition',
      recipe?.id,
      // Re-resolve when the ingredient list materially changes. Keep
      // the key small — comma-joined name|unit|amount tuples.
      recipe?.ingredients
        .map((i) =>
          i.type === 'MEASURED'
            ? `${i.name}|${i.quantity.unit}|${quantityKey(i.quantity)}`
            : `${i.name}|vague`,
        )
        .join(';'),
    ],
    enabled: !!recipe && !!user && localReady,
    queryFn: async (): Promise<ResolvedRecipeNutrition> => {
      if (!recipe) {
        return { rows: [], totals: emptyTotals(), needsReview: false };
      }
      // One conversion-rules fetch per recipe load — cheap, used by
      // every measured-ingredient row.
      const densityRules = user ? await listConversionRulesForOwner(user.id) : [];

      const rows: IngredientNutritionRow[] = [];
      let needsReview = false;

      for (const ing of recipe.ingredients) {
        if (ing.type !== 'MEASURED') {
          rows.push({
            ingredientId: ing.id,
            ingredientName: ing.name,
            grams: null,
            fact: null,
            approximate: false,
          });
          continue;
        }
        const key = ingredientLookupKey(ing.name);
        const mapping = await resolveMapping(key);
        let fact: NutritionFact | null = null;
        if (mapping) {
          fact = await readCachedFact(mapping.source, mapping.source_id);
          // A mapped row with no calorie data is worse than no mapping:
          // it contributes zero to every nutrient and quietly deflates
          // the whole recipe, and because the mapping short-circuits the
          // search it can never be recovered from. Several curated rows
          // point at USDA Foundation entries that carry no energy value
          // (73.8% of that tier does), so drop through to the search
          // instead — which now hard-filters those out.
          if (fact && fact.calories_kcal == null) fact = null;
        }
        if (!fact) {
          // No mapping yet OR cache miss. Try the local Foundation/SR
          // Legacy mirror first — sub-millisecond, no network. Almost
          // every generic cooking ingredient resolves here. Only fall
          // through to the edge function for cases the local snapshot
          // doesn't cover (Branded, or items added to USDA after our
          // last bulk-import refresh). Auto-match is provisional; the
          // user confirms via the override UI, so we don't persist a
          // mapping at this point.
          try {
            // "salt and pepper" names two foods. One row cannot stand in
            // for both and picking either is confidently wrong, so the
            // auto-match declines and the panel reports it as not
            // counted. This sits above BOTH searches — guarding only the
            // local one just meant the edge function answered instead.
            // The override dialog deliberately does not go through here:
            // a query the user typed themselves should always search.
            let hits: NutritionFact[] = [];
            if (!extractIngredientTerms(ing.name).compound) {
              hits = await searchLocalEssentials(ing.name, 5);
              if (hits.length === 0) hits = await searchNutrition(ing.name, 5);
            }
            // searchLocalEssentials filters these in SQL, but the remote
            // fallback does not, so a fall-through could still hand back
            // a row with no energy value — which contributes zero to
            // every nutrient and quietly deflates the recipe. Drop them
            // here so both sources obey the same rule.
            hits = hits.filter((h) => h.calories_kcal != null);
            fact = hits[0] ?? null;
            if (hits.length > 0) {
              needsReview = true;
              // The positive class for a future ingredient→food matcher.
              // Without it the signal table would hold only corrections, and a
              // model trained on corrections alone learns that every match is
              // wrong. Deduped per page-load on the ingredient key: the same
              // "butter" resolves on every recipe the user opens, and one
              // unreviewed impression per session is plenty.
              if (recordOnce(`nutrition:${key}`)) {
                recordSuggestionEvent({
                  surface: 'nutrition_match',
                  action: 'auto',
                  input_text: ing.name,
                  suggested_key: nutritionFactKey(hits[0]!),
                  chosen_key: nutritionFactKey(hits[0]!),
                  chosen_rank: 0,
                  candidates: hits.map(nutritionFactKey),
                });
              }
            }
          } catch (e) {
            console.warn('nutrition search failed', e);
          }
        }

        // Pre-narrow: pass null-named generics through plus any rule
        // whose name shares at least one token with the ingredient.
        // The math layer (`quantityToGrams`) does the real subset-
        // matching + most-specific selection; this filter just keeps
        // its inner loop short.
        const recipeTokens = new Set(tokenizeIngredient(ing.name));
        const ctx: ConversionContext = {
          densityRules: densityRules
            .filter((r) => {
              if (r.ingredientName == null) return true;
              const ruleTokens = tokenizeIngredient(r.ingredientName);
              return ruleTokens.some((t) => recipeTokens.has(t));
            })
            .map((r) => ({
              fromUnit: r.fromUnit,
              factor: r.factor,
              ingredientName: r.ingredientName,
            })),
          portions: fact?.portions ?? [],
          override: mapping?.custom_grams_per_unit,
        };
        const quantityAmount = quantityAmountToNumber(ing.quantity);
        const conv =
          quantityAmount == null
            ? null
            : quantityToGrams(quantityAmount, ing.quantity.unit, ing.name, ctx);

        rows.push({
          ingredientId: ing.id,
          ingredientName: ing.name,
          grams: conv?.grams ?? null,
          fact,
          approximate: conv?.approximate ?? false,
        });
      }
      const totals = totalNutrition(rows);
      return { rows, totals, needsReview };
    },
  });
}

function emptyTotals(): NutritionTotals {
  return {
    calories_kcal: 0,
    protein_g: 0,
    fat_g: 0,
    saturated_fat_g: 0,
    carbs_g: 0,
    sugar_g: 0,
    fiber_g: 0,
    sodium_mg: 0,
    total_grams: 0,
    resolved_count: 0,
    unresolved_count: 0,
    approximate_count: 0,
  };
}

// Domain Quantity is a discriminated union. Reduce to a single number
// for the gram calculation; ranges collapse to their midpoint.
function quantityAmountToNumber(q: Quantity): number | null {
  switch (q.type) {
    case 'EXACT':
      return q.amount;
    case 'FRACTIONAL':
      return (q.whole ?? 0) + (q.numerator ?? 0) / (q.denominator || 1);
    case 'RANGE':
      return (q.min + q.max) / 2;
  }
}

function quantityKey(q: Quantity): string {
  const n = quantityAmountToNumber(q);
  return n == null ? '?' : n.toFixed(3);
}
